import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Share2, Copy, Mail, MessageSquare, QrCode, Check } from "lucide-react";
import { toast } from "sonner";

export function brandOf(event) {
  const b = event?.branding || {};
  return {
    primary: b.primary_color || "#0B1E36",
    secondary: b.secondary_color || "#00E0C6",
    accent: b.accent_color || b.secondary_color || "#FFB703",
    logo: b.logo_url || null,
    teamPhoto: b.team_photo_url || null,
    school: b.school_name || event?.team_name || "",
  };
}

export function money(n) {
  return `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function luminance(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec((hex || "").trim());
  if (!m) return 0.5;
  const n = parseInt(m[1], 16);
  const r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
}

/** Text color that stays readable ON a brand-colored fill. */
export function textOn(bg) {
  return luminance(bg) > 0.45 ? "#0b1424" : "#ffffff";
}

/** Brand color for text/icons on the dark page bg — falls back to white if too dark. */
export function visibleOnDark(color) {
  return luminance(color) < 0.22 ? "#ffffff" : color;
}

export function FusionMeter({ raised, goal, color, testId = "fusion-meter" }) {
  const pct = goal > 0 ? Math.min(100, (raised / goal) * 100) : 0;
  const safe = visibleOnDark(color);
  return (
    <div data-testid={testId}>
      <div className="flex items-end justify-between mb-1.5">
        <div className="text-2xl font-black text-white">{money(raised)}</div>
        <div className="text-xs text-white/50">of {money(goal)} goal</div>
      </div>
      <div className="h-3 rounded-full bg-white/10 overflow-hidden">
        <motion.div
          className="h-full rounded-full"
          style={{ background: safe }}
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 1.2, ease: "easeOut" }}
        />
      </div>
      <div className="mt-1 text-[11px] font-bold" style={{ color: safe }}>{pct.toFixed(0)}% funded</div>
    </div>
  );
}

export function Countdown({ dateStr, color }) {
  const [left, setLeft] = useState(null);
  useEffect(() => {
    if (!dateStr) return;
    const tick = () => {
      const diff = new Date(dateStr).getTime() - Date.now();
      if (diff <= 0) return setLeft({ done: true });
      setLeft({
        d: Math.floor(diff / 86400000),
        h: Math.floor((diff % 86400000) / 3600000),
        m: Math.floor((diff % 3600000) / 60000),
        s: Math.floor((diff % 60000) / 1000),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [dateStr]);
  if (!dateStr || !left) return null;
  if (left.done) return <div className="text-sm font-bold" style={{ color }} data-testid="fusion-countdown">Event day is here!</div>;
  const Cell = ({ v, l }) => (
    <div className="text-center px-3 py-2 rounded-lg bg-white/5 border border-white/10 min-w-[56px]">
      <div className="text-xl font-black text-white tabular-nums">{String(v).padStart(2, "0")}</div>
      <div className="text-[9px] uppercase tracking-widest text-white/40">{l}</div>
    </div>
  );
  return (
    <div className="flex gap-2" data-testid="fusion-countdown">
      <Cell v={left.d} l="days" /><Cell v={left.h} l="hrs" /><Cell v={left.m} l="min" /><Cell v={left.s} l="sec" />
    </div>
  );
}

export function ShareControls({ url, title, color, onShare, compact = false }) {
  const [copied, setCopied] = useState(false);
  const [qr, setQr] = useState(false);
  const track = () => { try { onShare && onShare(); } catch {} };
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); setCopied(true); toast.success("Link copied"); setTimeout(() => setCopied(false), 2000); track(); }
    catch { toast.error("Could not copy link"); }
  };
  const native = async () => {
    if (navigator.share) { try { await navigator.share({ title, url }); track(); } catch {} }
    else copy();
  };
  const msg = encodeURIComponent(`${title} — pledge here: ${url}`);
  const btn = "inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-bold border border-white/15 bg-white/5 text-white hover:bg-white/10 transition-colors";
  return (
    <div data-testid="fusion-share-controls">
      <div className={`flex flex-wrap ${compact ? "gap-1.5" : "gap-2"}`}>
        <button className={btn} style={{ background: visibleOnDark(color), color: textOn(visibleOnDark(color)), borderColor: visibleOnDark(color) }} onClick={native} data-testid="share-native-btn">
          <Share2 className="h-3.5 w-3.5" /> Share this campaign
        </button>
        <button className={btn} onClick={copy} data-testid="share-copy-btn">
          {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />} Copy link
        </button>
        <a className={btn} href={`sms:?&body=${msg}`} onClick={track} data-testid="share-sms-btn">
          <MessageSquare className="h-3.5 w-3.5" /> Text
        </a>
        <a className={btn} href={`mailto:?subject=${encodeURIComponent(title)}&body=${msg}`} onClick={track} data-testid="share-email-btn">
          <Mail className="h-3.5 w-3.5" /> Email
        </a>
        <button className={btn} onClick={() => { setQr((v) => !v); track(); }} data-testid="share-qr-btn">
          <QrCode className="h-3.5 w-3.5" /> QR
        </button>
      </div>
      {qr && (
        <div className="mt-3 inline-block p-3 bg-white rounded-xl" data-testid="share-qr-image">
          <img
            src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(url)}`}
            alt="Campaign QR code" width={180} height={180}
          />
        </div>
      )}
    </div>
  );
}
